import { ChangeEvent, KeyboardEvent, MouseEvent, useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { IntervalType, Org, OrgType, RowId, SongListEntryType } from '../../../../../common/src/model.ts';
import { getWeeklySongListType } from '../../../../../common/src/model_util.ts';
import { capitalize, ensureUnreachable } from '../../../../../common/src/util.ts';
import BibleIcon from '../../../assets/bible.svg?react';
import EighthNoteIcon from '../../../assets/eighth-note.svg?react';
import PersonIcon from '../../../assets/person.svg?react';
import TrashIcon from '../../../assets/trash.svg?react';
import WedgeDownIcon from '../../../assets/wedge-down.svg?react';
import WedgeUpIcon from '../../../assets/wedge-up.svg?react';
import { LocalStorageKey, usabilityTooltipDisplayCount } from '../../../data/client_local_storage.ts';
import { useCurrentOrg } from '../../../data/use_current_org.tsx';
import { useLocalStorage } from '../../../data/use_local_storage.tsx';
import { useUserAttributes } from '../../../data/use_user_attributes.ts';
import { CheckBox } from '../../../util/check_box.tsx';
import { confirm } from '../../../util/confirm.tsx';
import { focusNextTabStop } from '../../../util/shared.tsx';
import { SpeechBubble, SpeechBubbleOrientation } from '../../../util/speech_bubble.tsx';
import { useEffectOnce } from '../../../util/use_effect_once.ts';
import {
  DateRowTop,
  DisplayUnauthorizedAlert,
  Menu,
  MenuEntry,
  PlannerYearHeight,
  SongCellPadding,
  SongRowHeightDouble,
  SongRowHeightSingle,
  SongRowWidth,
} from './shared.tsx';
import { setSongListSchemaLabel, setSongListSchemaRowAssigned, setSongListSchemaType } from './song_list_schema.ts';
import { AllSongLists } from './use_all_song_lists.ts';

interface Props {
  allSongLists: AllSongLists;
  songRowCount: number;
}

export function PlannerSchemaColumn({ allSongLists, songRowCount }: Props) {
  const { currentOrg } = useCurrentOrg();
  const { isInternalUser, isOrgAdmin } = useUserAttributes();
  const [newRowIndex, setNewRowIndex] = useState<number | undefined>(); // for selecting new rows
  return (
    <PlannerSchemaWrapper>
      <YearRow />
      <WeekRow>{getSchemaColumnTitle(currentOrg)}</WeekRow>
      {currentOrg?.type !== OrgType.School && (
        <SongSchemaRow
          key="hymn-of-the-month"
          rowIndex={-1}
          allSongLists={allSongLists}
          newRowIndex={newRowIndex}
          setNewRowIndex={setNewRowIndex}
          isInternalUser={isInternalUser}
          isOrgAdmin={isOrgAdmin}
        />
      )}
      {
        // TODO(hewitt): Using the index as the React key means the schema rows slide around underneath the React
        //               components, rather than properly inserting and removing React components for each row.
        //               Really, schema rows should have unique identifiers which would be used as React keys,
        //               then React would properly track schema row <-> DOM element.
        //               We can get away with this for now because the SongSchemaRow DOM element only relies
        //               on the index and contains a hack-job useEffect() to slide row names up/down.
        [...Array(songRowCount).keys()].map((index) => (
          <SongSchemaRow
            key={index + 1}
            rowIndex={index + 1}
            allSongLists={allSongLists}
            newRowIndex={newRowIndex}
            setNewRowIndex={setNewRowIndex}
            isOrgAdmin={isOrgAdmin}
            isInternalUser={isInternalUser}
          />
        ))
      }
    </PlannerSchemaWrapper>
  );
}

const PlannerSchemaWrapper = styled.div`
  z-index: 5; // above liturgy row content, but below year
  display: flex;
  flex-direction: column;
  position: sticky;
  left: 0;
`;

const YearRow = styled.div`
  height: ${PlannerYearHeight};
  position: sticky;
  top: 0;
  background-color: var(--color-background);
`;

function SongSchemaRow({
  allSongLists,
  rowIndex,
  newRowIndex,
  setNewRowIndex,
  isOrgAdmin,
  isInternalUser,
}: {
  allSongLists: AllSongLists;
  rowIndex: number;
  newRowIndex: number | undefined;
  setNewRowIndex: (newRowIndex: number | undefined) => void;
  isOrgAdmin: boolean;
  isInternalUser: boolean;
}) {
  const { currentOrg, updateOrgInfo } = useCurrentOrg();
  const schema = currentOrg?.churchLiturgySchema;
  const row = schema?.rows.find(({ row }) => row === rowIndex);
  const isHymnOfTheMonth = rowIndex < 0;
  const labelInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (rowIndex === newRowIndex) {
      labelInputRef.current?.focus();
      setNewRowIndex(undefined);
    }
  }, [labelInputRef, rowIndex, newRowIndex, setNewRowIndex]);

  useEffect(() => {
    // TODO(hewitt): Total hack-job. Schema rows should have unique IDs.
    //               See comment above where <SongSchemaRow> is instantiated with the index for a key.
    if (labelInputRef.current) {
      labelInputRef.current.value = row?.label ?? '';
    }
  }, [row?.label]);

  const onLabelBlur = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (!currentOrg) {
        return;
      }
      const label = event.target.value && event.target.value !== '' ? event.target.value : undefined;
      if (label !== row?.label) {
        void updateOrgInfo({ id: currentOrg.id, churchLiturgySchema: setSongListSchemaLabel(schema, rowIndex, label) });
      }
    },
    [currentOrg, row?.label, rowIndex, schema, updateOrgInfo],
  );

  const onKeyDown = useCallback((event: KeyboardEvent<HTMLInputElement>) => {
    // Treat <Enter> keypress like <Tab>
    if (event.key === 'Enter') {
      focusNextTabStop(event.target as HTMLInputElement);
    }
  }, []);

  const onMouseDown = useCallback(
    (event: MouseEvent<HTMLInputElement>) => {
      if (!isOrgAdmin && !isInternalUser) {
        void DisplayUnauthorizedAlert();
        event.preventDefault();
        return;
      }
    },
    [isOrgAdmin, isInternalUser],
  );

  return (
    <SongRowWrapper $isDoubleHeight={isHymnOfTheMonth}>
      <RowCellWrapper key="cell">
        {isHymnOfTheMonth ? (
          'Hymn of the Month'
        ) : (
          <RowInput
            defaultValue={row?.label}
            onMouseDown={onMouseDown}
            onBlur={onLabelBlur}
            onKeyDown={onKeyDown}
            placeholder={
              currentOrg?.type === OrgType.School ? getPlaceholderFromInterval(row?.intervalType) : undefined
            }
            ref={labelInputRef}
          />
        )}
      </RowCellWrapper>
      {!isHymnOfTheMonth &&
        (currentOrg?.type === OrgType.School ? (
          <AssignmentIcon rowIndex={rowIndex} isChecked={!!row?.assigned} />
        ) : (
          <TypeIcon
            allSongLists={allSongLists}
            key="icon"
            type={row?.type ?? SongListEntryType.Song}
            rowIndex={rowIndex}
            setNewRowIndex={setNewRowIndex}
            isOrgAdmin={isOrgAdmin}
            isInternalUser={isInternalUser}
          />
        ))}
    </SongRowWrapper>
  );
}

const SongRowWrapper = styled.div<{ $isDoubleHeight?: boolean }>`
  font-family: Jost-SemiBold, Arial, sans-serif;
  height: ${(props) => (props.$isDoubleHeight ? SongRowHeightDouble : SongRowHeightSingle)};
  width: ${SongRowWidth};
  outline: 1px solid var(--color-text); /* outline instead of border takes up 0 space */
  background-color: var(--color-planner-row-header);
  padding: ${SongCellPadding};
  display: flex;
  align-items: center;
`;

const WeekRow = styled(SongRowWrapper)`
  position: sticky;
  top: ${DateRowTop};
`;

const RowCellWrapper = styled.div`
  text-align: left;
`;

const TypeIconWidth = '15px';
const TypeIconHeight = '18px';

const RowInput = styled.input`
  background-color: transparent;
  border: none;
  width: calc(${SongRowWidth} - (2 * ${SongCellPadding}) - ${TypeIconWidth});
`;

function TypeIcon({
  allSongLists,
  rowIndex,
  setNewRowIndex,
  type,
  isOrgAdmin,
  isInternalUser,
}: {
  allSongLists: AllSongLists;
  rowIndex: number;
  setNewRowIndex: (newRowIndex: number) => void;
  type: SongListEntryType;
  isOrgAdmin: boolean;
  isInternalUser: boolean;
}) {
  const [menuOpen, setMenuOpen] = useState(false);

  const onClick = useCallback(
    (event: MouseEvent<HTMLInputElement>) => {
      if (!isOrgAdmin && !isInternalUser) {
        void DisplayUnauthorizedAlert();
        event.preventDefault();
        return;
      }
      setMenuOpen(!menuOpen);
    },
    [isOrgAdmin, isInternalUser, menuOpen],
  );

  const onBlur = useCallback(() => {
    setMenuOpen(false);
  }, []);

  return (
    <TypeIconWrapper tabIndex={rowIndex + 1000} onClick={onClick} onBlur={onBlur}>
      {getTypeIcon(type)}
      {menuOpen && (
        <Menu>
          {Object.values(SongListEntryType).map((item) => (
            <TypeMenuEntry key={item} type={item} rowIndex={rowIndex} />
          ))}
          <InsertMenuEntry
            allSongLists={allSongLists}
            isBefore={true}
            rowIndex={rowIndex}
            setNewRowIndex={setNewRowIndex}
          />
          <InsertMenuEntry
            allSongLists={allSongLists}
            isBefore={false}
            rowIndex={rowIndex}
            setNewRowIndex={setNewRowIndex}
          />
          <DeleteRow allSongLists={allSongLists} rowIndex={rowIndex} />
        </Menu>
      )}
    </TypeIconWrapper>
  );
}

function AssignmentIcon({ rowIndex, isChecked }: { rowIndex: RowId; isChecked: boolean }) {
  const { currentOrg, updateOrgInfo } = useCurrentOrg();
  const [tooltipCount, setTooltipCount] = useLocalStorage(LocalStorageKey.TooltipVisibilityCount);

  const onClick = useCallback(() => {
    if (!currentOrg) {
      return;
    }
    const schema = currentOrg?.churchLiturgySchema;
    void updateOrgInfo({
      id: currentOrg.id,
      churchLiturgySchema: setSongListSchemaRowAssigned(schema, rowIndex, !isChecked),
    });
  }, [currentOrg, isChecked, rowIndex, updateOrgInfo]);

  useEffectOnce(() => {
    if (rowIndex !== 1) {
      return;
    }
    setTooltipCount((tooltipCount) =>
      (tooltipCount.schoolAssignment ?? 0) < usabilityTooltipDisplayCount + 1
        ? { ...tooltipCount, schoolAssignment: (tooltipCount.schoolAssignment ?? 0) + 1 }
        : tooltipCount,
    );
  });

  return (
    <CheckBox onClick={onClick} isChecked={isChecked}>
      {rowIndex === 1 && (tooltipCount.schoolAssignment ?? 0) <= usabilityTooltipDisplayCount && (
        <SchoolAssignmentSpeechBubble $orientation={SpeechBubbleOrientation.Horizontal}>
          <SchoolAssignmentTooltip>Prioritize your Family Singing Plans</SchoolAssignmentTooltip>
        </SchoolAssignmentSpeechBubble>
      )}
    </CheckBox>
  );
}

function InsertMenuEntry({
  allSongLists,
  isBefore,
  rowIndex,
  setNewRowIndex,
}: {
  allSongLists: AllSongLists;
  isBefore: boolean;
  rowIndex: number;
  setNewRowIndex: (newRowIndex: number) => void;
}) {
  const { currentOrg, updateOrgInfo } = useCurrentOrg();
  const schema = currentOrg?.churchLiturgySchema;

  const onInsertRow = useCallback(async () => {
    const newRowIndex = isBefore ? rowIndex : rowIndex + 1;
    if (currentOrg && schema && schema.rows.length > 0) {
      await updateOrgInfo({
        id: currentOrg.id,
        churchLiturgySchema: {
          ...schema,
          rows: schema.rows.map((row) => (row.row >= newRowIndex ? { ...row, row: row.row + 1 } : row)),
        },
      });
    }
    await allSongLists.insertNewRowAt(newRowIndex);
    setNewRowIndex(newRowIndex);
  }, [allSongLists, currentOrg, isBefore, rowIndex, schema, setNewRowIndex, updateOrgInfo]);

  return (
    <MenuEntry $isFirst={false} $isLast={false} $borderTop={isBefore} $borderBottom={!isBefore} onClick={onInsertRow}>
      {isBefore ? <InsertBeforeIconWrapper /> : <InsertAfterIconWrapper />}
      Insert {isBefore ? 'Before' : 'After'}
    </MenuEntry>
  );
}

const TypeIconWrapper = styled.div``;

const TypeMenuEntry = function ({ type, rowIndex }: { type: SongListEntryType; rowIndex: number }) {
  const entryTypes = Object.values(SongListEntryType);
  const isFirst = entryTypes.indexOf(type) === 0;
  const isLast = entryTypes.indexOf(type) === entryTypes.length - 1;
  const { currentOrg, updateOrgInfo } = useCurrentOrg();

  const onClick = useCallback(() => {
    if (!currentOrg) {
      return;
    }
    const schema = currentOrg?.churchLiturgySchema;
    void updateOrgInfo({ id: currentOrg.id, churchLiturgySchema: setSongListSchemaType(schema, rowIndex, type) });
  }, [currentOrg, rowIndex, type, updateOrgInfo]);

  return (
    <MenuEntry $isFirst={isFirst} $isLast={false} $borderBottom={isLast} onClick={onClick}>
      {getTypeIcon(type)}
      {capitalize(type)}
    </MenuEntry>
  );
};

const TypeTextWrapper = styled.div`
  font-family: 'Times New Roman', Times, serif;
  font-size: 1.2rem;
  font-weight: bold;
  width: ${TypeIconWidth};
  cursor: pointer;
`;

const EighthNoteIconWrapper = styled(EighthNoteIcon)`
  width: ${TypeIconWidth};
  height: ${TypeIconHeight};
  cursor: pointer;
  fill: var(--color-text);
`;

const BibleIconWrapper = styled(BibleIcon)`
  width: ${TypeIconWidth};
  height: ${TypeIconHeight};
  cursor: pointer;
  fill: var(--color-text);
`;

const PersonIconWrapper = styled(PersonIcon)`
  width: ${TypeIconWidth};
  height: ${TypeIconHeight};
  cursor: pointer;
  fill: var(--color-text);
`;

function getTypeIcon(type: SongListEntryType) {
  switch (type) {
    case SongListEntryType.Song:
      return <EighthNoteIconWrapper />;
    case SongListEntryType.Assignment:
      return <PersonIconWrapper />;
    case SongListEntryType.Scripture:
      return <BibleIconWrapper />;
    case SongListEntryType.Text:
      return <TypeTextWrapper>T</TypeTextWrapper>;
    default:
      ensureUnreachable(type);
  }
}

function DeleteRow({ allSongLists, rowIndex }: { allSongLists: AllSongLists; rowIndex: number }) {
  const { currentOrg, updateOrgInfo } = useCurrentOrg();
  const row = currentOrg?.churchLiturgySchema?.rows.find(({ row }) => row === rowIndex);

  const getCountVerbiage = useCallback(() => {
    const entryCount = allSongLists.filter(
      ({ type, songs }) => type === getWeeklySongListType(currentOrg) && !!songs.find(({ row }) => row === rowIndex),
    ).length;
    if (entryCount === 0) {
      return '';
    } else if (entryCount === 1) {
      return ' and one cell value';
    } else if (entryCount === 2) {
      return ' and both cell values';
    } else {
      return ` and all ${entryCount} cell values`;
    }
  }, [allSongLists, currentOrg, rowIndex]);

  const onClick = useCallback(async () => {
    const labelVerbiage = row?.label ? ` “${row.label}”` : '';
    if (
      await confirm({
        confirmation: (
          <span>
            Delete row{labelVerbiage}
            {getCountVerbiage()}?
          </span>
        ),
      })
    ) {
      const schema = currentOrg?.churchLiturgySchema;
      if (currentOrg && schema && schema.rows.length > 0) {
        await updateOrgInfo({
          id: currentOrg.id,
          churchLiturgySchema: {
            ...schema,
            rows: schema.rows
              .filter(({ row }) => row !== rowIndex) // filter out matching row
              .map((row) => (row.row >= rowIndex ? { ...row, row: row.row - 1 } : row)), // bump following rows up
          },
        });
      }
      await allSongLists.deleteRowAt(rowIndex);
    }
  }, [allSongLists, currentOrg, getCountVerbiage, row, rowIndex, updateOrgInfo]);

  return (
    <DeleteMenuEntryWrapper $isFirst={false} $isLast={true} $borderTop={true} onClick={onClick}>
      <TrashIconWrapper />
      Delete Row
    </DeleteMenuEntryWrapper>
  );
}

const DeleteMenuEntryWrapper = styled(MenuEntry)`
  color: var(--color-trash);
  fill: var(--color-trash);
`;

const TrashIconWrapper = styled(TrashIcon)`
  width: ${TypeIconWidth};
  height: ${TypeIconHeight};
  cursor: pointer;
`;

const InsertBeforeIconWrapper = styled(WedgeUpIcon)`
  width: ${TypeIconWidth};
  height: ${TypeIconHeight};
  cursor: pointer;
  fill: var(--color-text);
`;

const InsertAfterIconWrapper = styled(WedgeDownIcon)`
  width: ${TypeIconWidth};
  height: ${TypeIconHeight};
  cursor: pointer;
  fill: var(--color-text);
`;

function getSchemaColumnTitle(church: Org | undefined) {
  return church?.type === OrgType.Church ? 'Sunday' : 'Date';
}

function getPlaceholderFromInterval(interval: IntervalType | undefined): string | undefined {
  if (!interval) {
    return;
  }
  switch (interval) {
    case IntervalType.Custom:
      return 'Hymn of the term';
    case IntervalType.Month:
      return 'Hymn of the month';
    case IntervalType.Week:
    case IntervalType.Year:
    case IntervalType.Season:
    case IntervalType.Fixed:
      return;
    default:
      ensureUnreachable(interval);
  }
}

const SchoolAssignmentSpeechBubble = styled(SpeechBubble)`
  left: 120%;
  top: -240%;
`;

const SchoolAssignmentTooltip = styled.div`
  width: 16ch;
  font-family: Jost, sans-serif;
  padding: 0.5rem 1rem;
  text-align: center;
`;
